import { isNmOcdFacilityId } from '../../shared/facilityApiSearchIntent';
export { isNmOcdFacilityId } from '../../shared/facilityApiSearchIntent';

export const normalizeIdentifierValue = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const INTERNAL_ID_PREFIXES = [
  'fac_',
  'ocd-facility-',
  'wda-',
  'nm-water-',
  'tx-permit-',
  'user-',
  'user_',
  'temp-',
  'incident-',
  'facility-',
  'id-',
  'new-facility-',
];

export const isLikelyInternalIdentifier = (value: unknown): boolean => {
  const raw = normalizeIdentifierValue(value);
  if (!raw) return false;
  const lower = raw.toLowerCase();

  if (INTERNAL_ID_PREFIXES.some(prefix => lower.startsWith(prefix))) {
    return true;
  }

  // UUID
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(raw)) {
    return true;
  }

  // CUID/ULID-like or similar long random IDs.
  if (/^[a-z0-9]{24,}$/i.test(raw) && !/^\d+$/.test(raw)) {
    return true;
  }

  return false;
};

export const sanitizeIdentifierValue = (value: unknown): string => {
  const raw = normalizeIdentifierValue(value);
  if (!raw) return '';
  return isLikelyInternalIdentifier(raw) ? '' : raw;
};

export const formatApiNumber = (value: unknown): string => {
  const raw = normalizeIdentifierValue(value);
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 10) return raw;
  return `${digits.slice(0, 2)}-${digits.slice(2, 5)}-${digits.slice(5)}`;
};

export const extractApiNumberFromText = (value: unknown): string => {
  const raw = normalizeIdentifierValue(value);
  if (!raw) return '';
  if (isLikelyApiNumber(raw)) return formatApiNumber(raw);
  const labeled = raw.match(/API\s*[:#]?\s*(\d{2}[-\s]?\d{3}[-\s]?\d{5}(?:[-\s]?\d{1,4})?)/i);
  if (labeled?.[1] && isLikelyApiNumber(labeled[1])) return formatApiNumber(labeled[1]);
  const dashed = raw.match(/\b(\d{2}-\d{3}-\d{5}(?:-\d{1,4})?)\b/);
  if (dashed?.[1] && isLikelyApiNumber(dashed[1])) return formatApiNumber(dashed[1]);
  const digits = raw.match(/\b(\d{10,14})\b/);
  if (digits?.[1] && isLikelyApiNumber(digits[1])) return formatApiNumber(digits[1]);
  return '';
};

export const extractApiNumberFromRecord = (record: Record<string, unknown> | null | undefined): string => {
  if (!record) return '';
  const fields = [
    record.api,
    record.apiNumber,
    record.api_number,
    record.API,
    record.identifier,
    record.id,
    record.wellName,
    record.name,
  ];
  for (const field of fields) {
    const hit = extractApiNumberFromText(field);
    if (hit) return hit;
  }
  return '';
};

export const stripEmbeddedApiFromName = (name: unknown, api?: string): string => {
  const raw = normalizeIdentifierValue(name);
  if (!raw) return '';
  let next = raw.replace(/\s*[·•|\-–]\s*API\s*[:#]?\s*\d{2}[-\s]?\d{3}[-\s]?\d{5}(?:[-\s]?\d{1,4})?/ig, '');
  const formatted = api ? formatApiNumber(api) : '';
  const digits = formatted.replace(/\D/g, '');
  if (formatted) next = next.split(formatted).join('');
  if (digits.length >= 10) next = next.split(digits).join('');
  next = next.replace(/\s{2,}/g, ' ').replace(/\s*[·•|]\s*$/g, '').trim();
  return next || raw;
};

export const isLikelyApiNumber = (value: unknown): boolean => {
  const raw = normalizeIdentifierValue(value);
  if (!raw) return false;
  if (isLikelyInternalIdentifier(raw)) {
    return false;
  }

  // API numbers are numeric (with optional separators).
  // NM/TX standard: 10 digits. With sidetrack/check suffix: up to 14 digits.
  if (/[a-z]/i.test(raw.replace(/[-\s]/g, ''))) return false;
  const digits = raw.replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 14) return false;

  // Accept any 10–14 digit purely-numeric identifier as an API number.
  // (NM=30 and TX=42 are most common, but don't reject others.)
  return digits.length >= 10 && digits.length <= 14;
};

export const extractFacilityIdentifier = (facility: Record<string, unknown>): string => {
  const api = extractApiNumberFromRecord(facility);
  if (api) return api;
  const candidates = [
    facility.api,
    facility.identifier,
    facility.api_number,
    facility.apiNumber,
    facility.ocdId,
    facility.permitNumber,
    facility.API,
  ];

  for (const candidate of candidates) {
    const value = sanitizeIdentifierValue(candidate);
    if (value) return value;
  }
  return '';
};

interface IdentifierPresentationInput {
  value?: unknown;
  type?: unknown;
  wellType?: unknown;
  facilityType?: unknown;
}

export interface IdentifierPresentation {
  label: string;
  value: string;
  copyValue: string;
  isApi: boolean;
}

export const getIdentifierPresentation = (
  input: IdentifierPresentationInput
): IdentifierPresentation => {
  const raw = sanitizeIdentifierValue(input.value);
  const typeHint = `${normalizeIdentifierValue(input.type)} ${normalizeIdentifierValue(input.wellType)} ${normalizeIdentifierValue(input.facilityType)}`.toLowerCase();
  const hasGasPlantHint = typeHint.includes('gas plant') || typeHint.includes('(gp)') || typeHint.includes('compressor') || typeHint.includes('(cs)');
  const hasPipelineHint = typeHint.includes('pipeline') || typeHint.includes('(plg)') || typeHint.includes('(plc)') || typeHint.includes('(plw)');

  const fallbackLabel = (() => {
    if (typeHint.includes('rig')) return 'Rig #';
    if (typeHint.includes('swd') || typeHint.includes('disposal') || typeHint.includes('permit')) return 'Permit';
    if (hasGasPlantHint) return 'Serial';
    if (hasPipelineHint) return 'Permit';
    if (typeHint.includes('facility')) return 'ID';
    if (typeHint.includes('incident')) return 'Incident ID';
    return 'API';
  })();

  if (!raw) {
    return { label: fallbackLabel, value: '', copyValue: '', isApi: false };
  }

  if (isLikelyApiNumber(raw)) {
    const formatted = formatApiNumber(raw);
    return {
      label: 'API',
      value: formatted || raw,
      copyValue: formatted || raw,
      isApi: true,
    };
  }

  if (isNmOcdFacilityId(raw)) {
    return { label: 'OCD ID', value: raw, copyValue: raw, isApi: false };
  }

  if (typeHint.includes('rig')) {
    return { label: 'Rig #', value: raw, copyValue: raw, isApi: false };
  }
  if (typeHint.includes('swd') || typeHint.includes('disposal') || typeHint.includes('permit')) {
    return { label: 'Permit', value: raw, copyValue: raw, isApi: false };
  }
  if (hasGasPlantHint) {
    return { label: 'Serial', value: raw, copyValue: raw, isApi: false };
  }
  if (hasPipelineHint) {
    return { label: 'Permit', value: raw, copyValue: raw, isApi: false };
  }
  if (typeHint.includes('facility')) {
    return { label: 'ID', value: raw, copyValue: raw, isApi: false };
  }
  if (typeHint.includes('incident')) {
    return { label: 'Incident ID', value: raw, copyValue: raw, isApi: false };
  }
  // Do not label non-API source or facility identifiers as API.
  if (typeHint.includes('well') || typeHint.includes('oil') || typeHint.includes('gas') || typeHint.includes('injection')) {
    return { label: 'ID', value: raw, copyValue: raw, isApi: false };
  }

  return { label: 'ID', value: raw, copyValue: raw, isApi: false };
};
